import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Field, useField, useFormikContext } from 'formik';
import { Badge, Flex, Spinner } from '@chakra-ui/react';

import { useUnitsAndMeasures } from 'newui/store';
import { RfxQuoteHeader } from 'newui/features/Rfx/Submission/components/QuoteTab/RfxQuoteHeader';
import { QuoteComment } from './QuoteComment';
import { QuoteSupportingDocuments } from './QuoteSupportingDocuments';
import { SupplierRowsSection } from './SupplierRowsSection';
import type { SupplierRowsSectionHandle } from './SupplierRowsSection';
import { DynamicQuoteTable } from '../../../components/QuoteTable';
import { cellDataToLookup, formatCurrency, getCellDiff, getQuoteDiff, mapSnapshotToRows, mapCellSnapshotToRows, mapQuoteTableRowsToReadOnly, buildReadOnlyRows, buildReadOnlyTotals, calculateReadOnlyColumnTotals, LEGACY_COLUMNS } from './utils';
import type { QuoteTableColumn, QuoteTableRow, SupplierRow } from '../../../components/QuoteTable/quote-table.types';
import { ReadOnlyQuoteTable } from './components/ReadOnlyQuoteTable';
import type { RfxQuoteItem, RfxQuoteTable } from '../../types/quote.type';
import type { QuoteRevisionSnapshot } from '../../types/revision.type';
import { RFX_SUBMISSION_STATUS } from '../../constants';
import { GST_RATE, GST_RATE_LABEL } from '../../../config/gst.config';
import { getAllQuoteRevisions, getQuoteRevisions, getSummaryTables } from '../../api';
import { SummaryTable } from './SummaryTable';
import type { SummaryTableConfig } from '../../types/quote.type';
import { UpdateQuoteModal } from './UpdateQuoteModal';
import { CompareRevisionsModal } from './CompareRevisionsModal';
import { LoadingSpinner } from 'components/Loading/LoadingSpinner';
import { ScheduleOfRates, ScheduleOfRatesHandle } from 'newui/features/Rfx/components/ScheduleOfRates';

export interface QuoteTabHandle {
    validateAndSave: () => Promise<boolean>;
}

export interface QuoteTabProps {
    disabled: boolean;
    inviteStatus: string;
    isClientView: boolean;
    listQuotes: RfxQuoteTable[];
    rfxId?: number;
    rfxInviteId?: number;
    onDraftRevisionChange?: (hasDraft: boolean) => void;
    onQuoteRevisionUpdated?: (isQuoteTabValid?: boolean) => void;
}

interface QuoteFieldValues {
    [key: string]: Record<string, number | string>;
}

const TABLE_HEADERS = [
    { name: '#', width: '3%' },
    { name: 'Description of Scope', width: '40%' },
    { name: 'Unit of Measurement', width: '19%' },
    { name: 'Quantity', width: '12%' },
    { name: 'Rate', width: '12%' },
    { name: 'Cost', width: '12%' },
];

const TOTAL_ROWS = [
    { id: 'subtotal', name: 'Subtotal', color: '#475467' },
    { id: 'gst', name: GST_RATE_LABEL, color: '#475467' },
    { id: 'totalcost', name: 'Total Cost', color: '#101828' },
] as const;

type TotalType = (typeof TOTAL_ROWS)[number]['id'];


const calculateTotal = (
    type: TotalType,
    keys: string[] | 'all',
    allValue: QuoteFieldValues,
    extraCost = 0
): number => {
    const keyIds =
        keys === 'all' ? Object.keys(allValue ?? {}) : keys;
    const subTotal = keyIds.reduce((acc, key) => {
        const rowValues = allValue?.[key] ?? {};
        // Sum all rate columns (rate_N keys) for this row
        const rateSum = Object.keys(rowValues).reduce((sum, field) => {
            if (field.startsWith('rate_')) {
                return sum + Number(rowValues[field] ?? 0);
            }
            return sum;
        }, 0);
        // Fall back to generic 'rate' if no column-specific keys exist
        const totalRate = rateSum > 0 ? rateSum : Number(rowValues.rate ?? 0);
        const quantity = Number(rowValues.quantity ?? 0);
        return acc + totalRate * quantity;
    }, 0) + extraCost;

    switch (type) {
        case 'subtotal':
            return subTotal;
        case 'gst':
            return subTotal * GST_RATE;
        case 'totalcost':
            return subTotal + subTotal * GST_RATE;
        default:
            return 0;
    }
};

const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === '-') {
        event.preventDefault();
    }
};

const handleNumberValue = (value: string | number): number | string => {
    const numericValue = parseFloat(String(value));
    if (isNaN(numericValue) || numericValue < 0) {
        return '';
    }
    return numericValue;
};

const mapRowToSupplierRow = (row: RfxQuoteItem, formikValues?: QuoteFieldValues, columns?: { id: number; columnType: string }[], localIdCounter?: React.MutableRefObject<number>): SupplierRow => {
    const counter = localIdCounter?.current ?? 1;
    const localId = `supplier-saved-${counter}`;
    if (localIdCounter) {
        localIdCounter.current += 1;
    }
    const key = `quote-${row.id}`;
    const cellValues = formikValues?.[key];

    // Populate columnValues from Formik state for each column
    const columnVals: Record<number, string> = {};
    if (cellValues && columns) {
        for (const col of columns) {
            if (col.columnType === 'response_value') {
                const val = cellValues[`rate_${col.id}`];
                if (val != null) {
                    columnVals[col.id] = String(val);
                }
            } else if (col.columnType === 'response_quantity') {
                const val = cellValues.quantity;
                if (val != null) {
                    columnVals[col.id] = String(val);
                }
            } else if (col.columnType === 'response_text') {
                const val = cellValues[`text_${col.id}`];
                if (val != null) {
                    columnVals[col.id] = String(val);
                }
            } else if (col.columnType === 'response_number') {
                const val = cellValues[`number_${col.id}`];
                if (val != null) {
                    columnVals[col.id] = String(val);
                }
            }
        }
    }

    return {
        localId,
        description: row.name || '',
        unitId: row.unitId ?? null,
        quantity: cellValues?.quantity != null ? String(cellValues.quantity) : '',
        rate: cellValues?.rate != null ? String(cellValues.rate) : '',
        savedId: row.id,
        columnValues: Object.keys(columnVals).length > 0 ? columnVals : undefined,
    };
};

const getClientRows = (rows: RfxQuoteItem[]): RfxQuoteItem[] =>
    rows.filter((r) => r.createdByType !== 'supplier');

const getSupplierRows = (rows: RfxQuoteItem[], inviteId?: number): RfxQuoteItem[] =>
    rows.filter((r) => r.createdByType === 'supplier' && (!inviteId || r.createdByInviteId === inviteId));

const QuoteTab = forwardRef<QuoteTabHandle, QuoteTabProps>(({
    isClientView,
    listQuotes,
    disabled,
    inviteStatus,
    rfxId,
    rfxInviteId,
    onDraftRevisionChange,
    onQuoteRevisionUpdated,
}, ref) => {
    const [, { value: values }] = useField<QuoteFieldValues>('quote_answer');
    const { setFieldValue } = useFormikContext();
    const { isLoading: isUnitMeasuresLoading, values: unitsAndMeasures } = useUnitsAndMeasures();

    const supplierRowRefs = useRef<Record<number, SupplierRowsSectionHandle | null>>({});
    const scheduleRef = useRef<ScheduleOfRatesHandle>(null);
    const localIdCounterRef = useRef(1);
    const [quoteValidationErrors, setQuoteValidationErrors] = useState<Set<number>>(new Set());
    const [scheduleValidationError, setScheduleValidationError] = useState(false);
    const [summaryTables, setSummaryTables] = useState<SummaryTableConfig[]>([]);
    // Cell-level validation errors: { [itemId]: { cells: { [colId]: true } } }
    const [clientRowErrors, setClientRowErrors] = useState<Record<number, { cells?: Record<number, boolean> }>>({});

    const validate = useCallback((): boolean => {
        const failedQuoteIds = new Set<number>();

        // Validate all supplier row sections — sets visual error indicators
        for (const [quoteId, handle] of Object.entries(supplierRowRefs.current)) {
            if (handle && !handle.validate()) {
                failedQuoteIds.add(Number(quoteId));
            }
        }

        // Validate client row response cells
        const cellErrors: Record<number, { cells?: Record<number, boolean> }> = {};
        if (!isClientView) {
            listQuotes?.forEach((quote) => {
                if (!quote.columns?.length) {
                    return;
                }
                const clientRows = getClientRows(quote.rows);
                const supplierEditableCols = quote.columns.filter(
                    (c) => c.isSupplierEditable && (
                        c.columnType === 'response_value' ||
                        c.columnType === 'response_quantity' ||
                        c.columnType === 'response_number'
                    )
                );
                for (const row of clientRows) {
                    const key = `quote-${row.id}`;
                    const rowValues = values?.[key] ?? {};
                    const emptyCells: Record<number, boolean> = {};
                    for (const col of supplierEditableCols) {
                        let fieldKey: string;
                        if (col.columnType === 'response_value') {
                            fieldKey = `rate_${col.id}`;
                        } else if (col.columnType === 'response_quantity') {
                            fieldKey = 'quantity';
                        } else {
                            fieldKey = `number_${col.id}`;
                        }
                        const val = rowValues[fieldKey];
                        if (val === undefined || val === null || val === '') {
                            emptyCells[col.id] = true;
                        }
                    }
                    if (Object.keys(emptyCells).length > 0) {
                        cellErrors[row.id] = { cells: emptyCells };
                        failedQuoteIds.add(quote.id);
                    }
                }
            });
        }
        setClientRowErrors(cellErrors);

        // Validate schedule of rates
        const scheduleInvalid = scheduleRef.current && !scheduleRef.current.validate();

        setQuoteValidationErrors(failedQuoteIds);
        setScheduleValidationError(!!scheduleInvalid);

        return failedQuoteIds.size === 0 && !scheduleInvalid;
    }, [isClientView, listQuotes, values]);

    useImperativeHandle(ref, () => ({
        validateAndSave: async (): Promise<boolean> => {
            const isValid = validate();

            // Save schedule of rates
            if (scheduleRef.current) {
                const saved = await scheduleRef.current.save();
                if (!saved) {
                    return false;
                }
            }

            return isValid;
        },
    }), [isClientView, listQuotes, values]);

    const handleFormikFieldChange = useCallback((itemId: number, field: string, value: number | string) => {
        setFieldValue(`quote_answer.quote-${itemId}.${field}`, value);
        // Clear validation error for this cell when user enters a value
        if (value !== '' && value !== 0) {
            // Extract column ID from field key (e.g., "rate_5" → 5)
            const match = field.match(/^(?:rate|text|number|qty)_(\d+)$/);
            if (match) {
                const colId = Number(match[1]);
                setClientRowErrors((prev) => {
                    const itemErrors = prev[itemId];
                    if (!itemErrors?.cells?.[colId]) {
                        return prev;
                    }
                    const { [colId]: _, ...remainingCells } = itemErrors.cells;
                    if (Object.keys(remainingCells).length === 0) {
                        const { [itemId]: __, ...rest } = prev;
                        return rest;
                    }
                    return { ...prev, [itemId]: { cells: remainingCells } };
                });
            }
        }
    }, [setFieldValue]);
    const [isQuotesLoading, setIsLoading] = useState(true);
    const [showUpdateQuoteModal, setShowUpdateQuoteModal] = useState(false);
    const [updateQuoteData, setUpdateQuoteData] = useState<{
        current: null | QuoteRevisionSnapshot;
        previous: null | QuoteRevisionSnapshot;
        quote: null | RfxQuoteTable;
    }>({ current: null, previous: null, quote: null });
    const [showCompareModal, setShowCompareModal] = useState(false);
    const [compareQuote, setCompareQuote] = useState<RfxQuoteTable | null>(null);
    const [draftRevisions, setDraftRevisions] = useState<Record<number, QuoteRevisionSnapshot>>({});
    const [refreshingQuoteId, setRefreshingQuoteId] = useState<number | null>(null);
    const [quoteDiffKeys, setQuoteDiffKeys] = useState<Record<number, Set<string>>>({});
    const [supplierRowCosts, setSupplierRowCosts] = useState<Record<number, number>>(() => {
        const initial: Record<number, number> = {};
        listQuotes?.forEach((quote) => {
            const sRows = getSupplierRows(quote.rows, rfxInviteId);
            if (sRows.length > 0) {
                const valueColIds = new Set(
                    quote.columns
                        ?.filter((c) => c.columnType === 'response_value')
                        .map((c) => c.id) ?? []
                );
                initial[quote.id] = sRows.reduce((acc, row) => {
                    const key = `quote-${row.id}`;
                    const rowValues = values?.[key] ?? {};
                    // Sum all rate_N values for response_value columns
                    const rateSum = [...valueColIds].reduce((sum, colId) => {
                        return sum + (Number(rowValues[`rate_${colId}`]) || 0);
                    }, 0);
                    // Fall back to generic rate * quantity if no column-specific values
                    if (rateSum === 0) {
                        const rate = Number(rowValues.rate ?? 0);
                        const quantity = Number(rowValues.quantity ?? 0);
                        return acc + rate * quantity;
                    }
                    return acc + rateSum;
                }, 0);
            }
        });
        return initial;
    });

    const [supplierColTotals, setSupplierColTotals] = useState<Record<number, Record<number, number>>>(() => {
        const initial: Record<number, Record<number, number>> = {};
        listQuotes?.forEach((quote) => {
            const sRows = getSupplierRows(quote.rows, rfxInviteId);
            if (sRows.length > 0) {
                const valueColIds = new Set(
                    quote.columns
                        ?.filter((c) => c.columnType === 'response_value')
                        .map((c) => c.id) ?? []
                );
                const perCol: Record<number, number> = {};
                for (const colId of valueColIds) {
                    perCol[colId] = 0;
                }
                for (const row of sRows) {
                    const key = `quote-${row.id}`;
                    const rowValues = values?.[key] ?? {};
                    for (const colId of valueColIds) {
                        const val = Number(rowValues[`rate_${colId}`]) || 0;
                        perCol[colId] = (perCol[colId] ?? 0) + val;
                    }
                }
                initial[quote.id] = perCol;
            }
        });
        return initial;
    });

    const handleSupplierRowsChange = useCallback((quoteId: number, rows: SupplierRow[]) => {
        // Find response_value column IDs for this quote
        const quote = listQuotes?.find((q) => q.id === quoteId);
        const valueColIds = new Set(
            quote?.columns
                ?.filter((c) => c.columnType === 'response_value')
                .map((c) => c.id) ?? []
        );
        let cost = 0;
        const perCol: Record<number, number> = {};
        for (const colId of valueColIds) {
            perCol[colId] = 0;
        }
        for (const row of rows) {
            const colValues = row.columnValues ?? {};
            for (const colId of valueColIds) {
                const val = Number(colValues[colId]) || 0;
                perCol[colId] = (perCol[colId] ?? 0) + val;
                cost += val;
            }
        }
        setSupplierColTotals((prev) => ({ ...prev, [quoteId]: perCol }));
        setSupplierRowCosts((prev) => {
            if (prev[quoteId] === cost) {
                return prev;
            }
            return { ...prev, [quoteId]: cost };
        });
    }, [listQuotes]);

    const isBeingRevised = inviteStatus === RFX_SUBMISSION_STATUS.BEING_REVISED;

    // Fetch draft revisions per-quote when in BEING_REVISED status
    const fetchDraftRevisions = useCallback(async () => {
        if (!isBeingRevised || !rfxInviteId || !listQuotes?.length) {
            setDraftRevisions({});
            setIsLoading(false);
            return;
        }
        try {
            const allRevisions = await getAllQuoteRevisions(rfxInviteId);
            const results: Record<number, QuoteRevisionSnapshot> = {};
            for (const quote of listQuotes) {
                const revisions = allRevisions[quote.id] ?? [];
                const drafts = revisions.filter((r) => !r.is_submitted && r.revision > 0);
                const draft = drafts.length ? drafts.reduce((a, b) => (a.revision > b.revision ? a : b)) : undefined;
                if (draft) {
                    results[quote.id] = draft;
                }
            }
            setDraftRevisions(results);
        } catch {
            setDraftRevisions({});
        } finally {
            setIsLoading(false);
            setRefreshingQuoteId(null);
        }
    }, [isBeingRevised, rfxInviteId, listQuotes]);

    useEffect(() => {
        setIsLoading(true);
        fetchDraftRevisions();
    }, [fetchDraftRevisions]);

    const hasDrafts = Object.keys(draftRevisions).length > 0;

    useEffect(() => {
        onDraftRevisionChange?.(hasDrafts);
    }, [hasDrafts, onDraftRevisionChange]);

    // Run validation on initial load to highlight invalid fields immediately
    useEffect(() => {
        if (!isQuotesLoading && !isClientView && listQuotes?.length) {
            validate();
        }
    }, [isQuotesLoading, isClientView, listQuotes, validate]);

    // Fetch summary tables for this RFx
    useEffect(() => {
        if (!rfxId) {
            return;
        }
        getSummaryTables(rfxId).then(setSummaryTables).catch(() => setSummaryTables([]));
    }, [rfxId]);

    // Fetch previous revision diffs for read-only view when quotes have revisions
    useEffect(() => {
        if (!disabled || !rfxInviteId || !listQuotes?.length) {
            return;
        }

        // Include quotes that have submitted revisions OR have draft revisions (BEING_REVISED)
        const quotesWithRevisions = listQuotes.filter(
            (q) => (q.has_revisions && q.current_revision > 0) || draftRevisions[q.id]
        );
        if (quotesWithRevisions.length === 0) {
            return;
        }

        const fetchDiffs = async () => {
            try {
                const allRevisions = await getAllQuoteRevisions(rfxInviteId);
                const diffs: Record<number, Set<string>> = {};
                for (const quote of quotesWithRevisions) {
                    const revisions = allRevisions[quote.id] ?? [];
                    const sorted = [...revisions].sort((a, b) => b.revision - a.revision);
                    const current = isClientView ? sorted.find((r) => r.is_submitted) : sorted[0];
                    const previous = sorted.find((r) => r.is_submitted && r.revision < (current?.revision ?? 0));
                    if (current && previous) {
                        // Use cell-based diff when revisions have cells, otherwise legacy answer diff
                        if (current.cells?.length || previous.cells?.length) {
                            diffs[quote.id] = getCellDiff(
                                cellDataToLookup(current.cells ?? []),
                                cellDataToLookup(previous.cells ?? [])
                            );
                        } else {
                            diffs[quote.id] = getQuoteDiff(current, previous);
                        }
                    }
                }
                setQuoteDiffKeys(diffs);
            } catch {
                // Error handled by global interceptor
            }
        };

        fetchDiffs();
    }, [disabled, rfxInviteId, listQuotes, isClientView, draftRevisions]);

    const allItemKeys = useMemo(
        () =>
            listQuotes?.flatMap((quote) =>
                getClientRows(quote.rows).map((row) => `quote-${row.id}`)
            ) ?? [],
        [listQuotes]
    );

    // Compute overall totals, substituting draft revision figures when applicable
    const overallTotals = useMemo(() => {
        let subtotal = 0;

        listQuotes?.forEach((quote) => {
            const clientRows = getClientRows(quote.rows);
            const draftRevision = draftRevisions[quote.id];
            if (draftRevision && isBeingRevised && !isClientView) {
                const rows = mapSnapshotToRows(draftRevision, clientRows);
                subtotal += rows.reduce((acc, row) => acc + row.quantity * row.rate, 0);
            } else {
                clientRows.forEach((row) => {
                    const key = `quote-${row.id}`;
                    const rate = Number(values?.[key]?.rate ?? 0);
                    const quantity = Number(values?.[key]?.quantity ?? 0);
                    subtotal += rate * quantity;
                });
            }
            subtotal += supplierRowCosts[quote.id] ?? 0;
        });

        return {
            subtotal,
            gst: subtotal * GST_RATE,
            totalcost: subtotal + subtotal * GST_RATE,
        };
    }, [listQuotes, draftRevisions, isBeingRevised, isClientView, values, supplierRowCosts]);

    const hasAnyTotalColumn = useMemo(() => {
        return listQuotes?.some((q) => q.columns?.some((c) => c.columnType === 'total')) ?? false;
    }, [listQuotes]);

    // Build a map from quote table ID → { columnName → columnId } for response_value columns
    const quoteColumnNameToId = useMemo(() => {
        const result: Record<number, Record<string, number>> = {};
        listQuotes?.forEach((quote) => {
            const map: Record<string, number> = {};
            quote.columns
                ?.filter((c) => c.columnType === 'response_value')
                .forEach((c) => {
                    map[c.name] = c.id;
                });
            result[quote.id] = map;
        });
        return result;
    }, [listQuotes]);

    // Compute live cell values for summary tables from Formik state or draft revision cells
    const summaryLiveCellValues = useMemo(() => {
        if (!summaryTables.length || !listQuotes?.length) {
            return {};
        }
        const result: Record<number, Record<number, Record<string, number>>> = {};

        for (const st of summaryTables) {
            const perTable: Record<number, Record<string, number>> = {};

            for (const qtId of st.quote_table_ids) {
                const quote = listQuotes.find((q) => q.id === qtId);
                if (!quote) {
                    continue;
                }
                const columnMap = quoteColumnNameToId[qtId] ?? {};
                const perColumn: Record<string, number> = {};
                const draft = draftRevisions[qtId];
                const draftCellLookup = draft?.cells?.length
                    ? cellDataToLookup(draft.cells)
                    : null;

                // Collect all row IDs to sum: quote.rows plus any extra rows in the draft
                const knownRowIds = new Set(quote.rows.map((r) => r.id));
                const allRowIds = [...knownRowIds];
                if (draftCellLookup) {
                    for (const rowId of Object.keys(draftCellLookup).map(Number)) {
                        if (!knownRowIds.has(rowId)) {
                            allRowIds.push(rowId);
                        }
                    }
                }

                // Identify supplier row IDs so we can skip them in the Formik/draft loop
                // (their values are tracked separately via supplierColTotals)
                const supplierRowIds = new Set(
                    getSupplierRows(quote.rows, rfxInviteId ?? 0).map((r) => r.id)
                );

                for (const colName of st.value_column_names) {
                    const colId = columnMap[colName];
                    if (colId === undefined) {
                        continue;
                    }
                    // Sum client rows from Formik/draft
                    let sum = 0;
                    for (const rowId of allRowIds) {
                        if (supplierRowIds.has(rowId)) {
                            continue;
                        }
                        let val: number;
                        if (draftCellLookup && draftCellLookup[rowId] !== undefined) {
                            val = Number(draftCellLookup[rowId]?.[colId] ?? 0);
                        } else {
                            const key = `quote-${rowId}`;
                            const rowValues = values?.[key];
                            val = Number(rowValues?.[`rate_${colId}`] ?? 0);
                        }
                        if (!isNaN(val)) {
                            sum += val;
                        }
                    }
                    // Add supplier row totals (managed by SupplierRowsSection)
                    sum += supplierColTotals[qtId]?.[colId] ?? 0;
                    perColumn[colName] = sum;
                }

                perTable[qtId] = perColumn;
            }

            result[st.id] = perTable;
        }

        return result;
    }, [summaryTables, listQuotes, quoteColumnNameToId, values, draftRevisions, supplierColTotals, rfxInviteId]);

    const createRevision = (snapshot: QuoteRevisionSnapshot) => {
        const newRevision = snapshot.revision + 1;
        return { ...snapshot, label: `Revision ${newRevision}`, is_submitted: false, revision: newRevision };
    }

    const handleOpenReviseQuote = useCallback(async (quote: RfxQuoteTable) => {
        if (!rfxInviteId) {
            return;
        }
        try {
            const revisions = await getQuoteRevisions({ inviteId: rfxInviteId, quoteId: quote.id });
            const sorted = [...revisions].sort((a, b) => b.revision - a.revision);

            const isLatestSubmitted = sorted[0].is_submitted;

            // if latest revision has been submitted, we need a new current submission
            const currentRevision = isLatestSubmitted ? createRevision(sorted[0]) : sorted[0];

            // if latest has been submitted, its the previous, otherwise its the one before this
            const previousRevision = isLatestSubmitted ? sorted[0] : sorted[1];

            if (currentRevision && previousRevision) {
                setUpdateQuoteData({ current: currentRevision, previous: previousRevision, quote });
                setShowUpdateQuoteModal(true);
            }
        } catch {
            // Error handled by global interceptor
        }
    }, [rfxInviteId]);

    const unitLookup = useMemo(() => {
        if (!unitsAndMeasures?.length) {
            return undefined;
        }
        return new Map(unitsAndMeasures.map((u) => [u.value, u.label]));
    }, [unitsAndMeasures]);

    const getQuoteComment = (quote: RfxQuoteTable): string => {
        // Comments are eager-loaded and scoped to the current invite by the backend
        return quote.comments?.[0]?.comment ?? "";
    };

    const getQuoteAttachments = (quote: RfxQuoteTable) => {
        // Attachments are eager-loaded and scoped to the current invite by the backend
        return (quote.attachments ?? []).map((a) => ({
            id: a.id,
            file_name: a.file_name,
            file_path: a.file_path,
            file_size: a.file_size,
            file_type: a.file_type,
        }));
    };

    if (isQuotesLoading) {
        return <LoadingSpinner />
    }

    return (
        <>
            {showUpdateQuoteModal && updateQuoteData.current && updateQuoteData.previous && updateQuoteData.quote && (
                <UpdateQuoteModal
                    isOpen={showUpdateQuoteModal}
                    onClose={() => setShowUpdateQuoteModal(false)}
                    inviteId={rfxInviteId}
                    currentData={updateQuoteData.current}
                    previousData={updateQuoteData.previous}
                    quote={updateQuoteData.quote}
                    onSaveSuccess={(isQuoteTabValid?: boolean) => {
                        onQuoteRevisionUpdated?.(isQuoteTabValid);
                        setRefreshingQuoteId(updateQuoteData.quote?.id ?? null);
                        fetchDraftRevisions();
                    }}
                />
            )}
            {showCompareModal && compareQuote && rfxInviteId && (
                <CompareRevisionsModal
                    isOpen={showCompareModal}
                    onClose={() => setShowCompareModal(false)}
                    columns={compareQuote.columns}
                    inviteId={rfxInviteId}
                    quoteId={compareQuote.id}
                    quoteName={compareQuote.name}
                    quoteItems={compareQuote.rows}
                    initialRevisionA={compareQuote.current_revision}
                    initialRevisionB={compareQuote.current_revision > 0 ? compareQuote.current_revision - 1 : 0}
                />
            )}
            {listQuotes?.map((quote) => {
                const draftRevision = draftRevisions[quote.id];
                const totalRevisions = draftRevision
                    ? draftRevision.revision
                    : quote.current_revision;
                const clientRows = getClientRows(quote.rows);
                const supplierRows = getSupplierRows(quote.rows, rfxInviteId);
                const initialSupplierRows: SupplierRow[] = supplierRows.map((r) => mapRowToSupplierRow(r, values, quote.columns, localIdCounterRef));

                return (
                    <RfxQuoteHeader
                        key={`quote-section-${quote.id}`}
                        isBeingRevised={!isClientView && isBeingRevised}
                        hasRevisions={quote.has_revisions}
                        hasValidationError={quoteValidationErrors.has(quote.id)}
                        onOpenReviseQuote={handleOpenReviseQuote}
                        onCompareRevisions={(q) => {
                            setCompareQuote(q);
                            setShowCompareModal(true);
                        }}
                        quote={quote}
                    >
                        {quote.has_revisions && !draftRevision && (
                            <Flex
                                className="mb-3"
                                align="center"
                                gap={2}
                            >
                                <Badge
                                    colorScheme="blue"
                                    data-testid={`revision-badge-${quote.id}`}
                                >
                                    Revision {quote.current_revision} of {totalRevisions}
                                </Badge>
                            </Flex>
                        )}
                        {draftRevision && isBeingRevised && (
                            <Flex
                                className="mb-3"
                                align="center"
                                gap={2}
                            >
                                <Badge
                                    colorScheme="orange"
                                    data-testid={`draft-revision-badge-${quote.id}`}
                                >
                                    Revision {draftRevision.revision}
                                </Badge>
                            </Flex>
                        )}
                        {disabled ? (
                            (() => {
                                const cols: QuoteTableColumn[] = quote.columns?.length
                                    ? quote.columns.map((c) => ({
                                        id: c.id,
                                        columnType: c.columnType,
                                        label: c.name,
                                        sortOrder: c.sortOrder,
                                        isDefault: c.isClientEditable,
                                        isSupplierEditable: c.isSupplierEditable,
                                    }))
                                    : LEGACY_COLUMNS;

                                // When a draft revision exists, show its values instead of Formik values
                                let allRows;
                                if (draftRevision) {
                                    const hasCells = Boolean(draftRevision.cells?.length);
                                    if (hasCells) {
                                        const cellLookup = cellDataToLookup(draftRevision.cells ?? []);
                                        const draftRows = mapCellSnapshotToRows(cellLookup, clientRows);
                                        allRows = mapQuoteTableRowsToReadOnly(draftRows, cols);
                                    } else {
                                        const draftRows = mapSnapshotToRows(draftRevision, clientRows);
                                        allRows = mapQuoteTableRowsToReadOnly(draftRows, cols);
                                    }
                                } else {
                                    allRows = [
                                        ...buildReadOnlyRows(clientRows, cols, values, unitLookup),
                                        ...buildReadOnlyRows(supplierRows, cols, values, unitLookup, true),
                                    ];
                                }

                                // Σ per-column totals (when procurer enabled includeTotalsRow)
                                let colTotals: Record<number, number> | null = null;
                                if (quote.includeTotalsRow) {
                                    if (draftRevision?.cells?.length) {
                                        // Compute from draft cells; supplier rows without draft cells use supplierColTotals
                                        const draftLookup = cellDataToLookup(draftRevision.cells);
                                        const responseValueCols = cols.filter((c) => c.columnType === 'response_value');
                                        const supplierRowIdSet = new Set(supplierRows.map((r) => r.id));
                                        const totalsMap: Record<number, number> = {};
                                        for (const col of responseValueCols) {
                                            let sum = 0;
                                            let supplierSummedFromDraft = false;
                                            for (const row of [...clientRows, ...supplierRows]) {
                                                if (draftLookup[row.id] !== undefined) {
                                                    const val = Number(draftLookup[row.id]?.[col.id] ?? 0);
                                                    if (!isNaN(val)) {
                                                        sum += val;
                                                    }
                                                    if (supplierRowIdSet.has(row.id)) {
                                                        supplierSummedFromDraft = true;
                                                    }
                                                } else if (!supplierRowIdSet.has(row.id)) {
                                                    // Client row not in draft — read from Formik
                                                    const formikVal = values?.[`quote-${row.id}`]?.[`rate_${col.id}`];
                                                    const val = Number(formikVal ?? 0);
                                                    if (!isNaN(val)) {
                                                        sum += val;
                                                    }
                                                }
                                            }
                                            // If no supplier rows were in the draft, add from supplierColTotals
                                            if (!supplierSummedFromDraft) {
                                                sum += supplierColTotals[quote.id]?.[col.id] ?? 0;
                                            }
                                            totalsMap[col.id] = sum;
                                        }
                                        colTotals = totalsMap;
                                    } else {
                                        colTotals = calculateReadOnlyColumnTotals(
                                            cols,
                                            clientRows,
                                            values,
                                            supplierColTotals[quote.id]
                                        );
                                    }
                                }

                                // Summary rows (only when table has a 'total' column type)
                                const hasTotalColumn = cols.some((c) => c.columnType === 'total');
                                const subtotal = hasTotalColumn
                                    ? calculateTotal('subtotal', 'all', values, supplierRowCosts[quote.id] ?? 0)
                                    : 0;

                                const isRefreshing = refreshingQuoteId === quote.id;

                                return (
                                    <div className="relative">
                                        {isRefreshing && (
                                            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-white/70">
                                                <Spinner size="lg" color="blue.500" />
                                            </div>
                                        )}
                                        <ReadOnlyQuoteTable
                                            columns={cols}
                                            columnTotals={colTotals}
                                            rows={allRows}
                                            diffKeys={quoteDiffKeys[quote.id]}
                                            totals={hasTotalColumn ? buildReadOnlyTotals(subtotal) : undefined}
                                        />
                                    </div>
                                );
                            })()
                        ) : quote.columns && quote.columns.length > 0 ? (
                            <DynamicQuoteTable
                                columns={quote.columns.map((c) => ({
                                    id: c.id,
                                    columnType: c.columnType,
                                    label: c.name,
                                    sortOrder: c.sortOrder,
                                    isDefault: c.isClientEditable,
                                    isSupplierEditable: c.isSupplierEditable,
                                }))}
                                disabled={disabled}
                                includeTotalsRow={quote.includeTotalsRow === true}
                                items={clientRows}
                                mode={isClientView ? "view" : "respond"}
                                supplierColumnTotals={supplierColTotals[quote.id]}
                                allValues={values}
                                rowErrors={clientRowErrors}
                                onFormikFieldChange={handleFormikFieldChange}
                                supplierRowsRef={rfxInviteId ? (handle: SupplierRowsSectionHandle | null) => { supplierRowRefs.current[quote.id] = handle; } : undefined}
                                supplierRowsProps={rfxInviteId ? {
                                    columns: quote.columns.map((c) => ({
                                        id: c.id,
                                        columnType: c.columnType,
                                        label: c.name,
                                        sortOrder: c.sortOrder,
                                        isDefault: c.isClientEditable,
                                        isSupplierEditable: c.isSupplierEditable,
                                    })),
                                    inviteId: rfxInviteId,
                                    quoteId: quote.id,
                                    startIndex: clientRows.length,
                                    initialRows: initialSupplierRows,
                                    onRowsChange: (rows: SupplierRow[]) => handleSupplierRowsChange(quote.id, rows),
                                    onFormikFieldChange: handleFormikFieldChange,
                                } : undefined}
                                totals={quote.columns?.some((c) => c.columnType === 'total')
                                    ? TOTAL_ROWS.map((row) => ({
                                        id: row.id,
                                        name: row.name,
                                        color: row.color,
                                        value: calculateTotal(
                                            row.id,
                                            clientRows.map((r) => `quote-${r.id}`),
                                            values,
                                            supplierRowCosts[quote.id] ?? 0
                                        ),
                                    }))
                                    : []}
                            />
                        ) : (
                            <table className="w-full table-auto rounded-xl border-b border-solid border-[#EAECF0] bg-white">
                                <thead className="h-[44px] bg-[#F9FAFB]">
                                    <tr className="border-b border-[#EAECF0]">
                                        {TABLE_HEADERS.map((th) => (
                                            <th
                                                key={th.name}
                                                className="bg-[#F9FAFB] text-left font-roboto text-[12px] font-medium text-tertiary-600"
                                                style={{ width: th.width }}
                                            >
                                                <div className="ml-5 mt-[3px]">{th.name}</div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {clientRows.map((singleData, itemIndex) => (
                                        <tr
                                            key={`quote-item-${singleData.id}`}
                                            className="border-b border-[#EAECF0]"
                                        >
                                            <td
                                                className="border border-[#EAECF0] px-4 py-2 text-center font-roboto font-normal text-primary-500"
                                                style={{ background: '#F9FAFB', height: '48px' }}
                                            >
                                                {itemIndex + 1}
                                            </td>
                                            <td
                                                className="relative border border-[#EAECF0] font-roboto"
                                                style={{ color: '#333', backgroundColor: '#fafafa' }}
                                            >
                                                <Field
                                                    name={`quote_answer[quote-${singleData.id}][name]`}
                                                >
                                                    {() => (
                                                        <p className="p-2">{singleData?.name || '-'}</p>
                                                    )}
                                                </Field>
                                            </td>
                                            <td
                                                className="relative border border-[#EAECF0] font-roboto"
                                                style={{ color: '#333', backgroundColor: '#fafafa' }}
                                            >
                                                {isUnitMeasuresLoading && (
                                                    <Spinner
                                                        size="sm"
                                                        className="ml-2 mt-1"
                                                    />
                                                )}
                                                {!isUnitMeasuresLoading && (
                                                    <Field
                                                        name={`quote_answer[quote-${singleData.id}][unit]`}
                                                    >
                                                        {({ field }: { field: { value: number } }) => (
                                                            <p className="px-2">
                                                                {
                                                                    unitsAndMeasures?.find(
                                                                        (unit) => unit.value === field?.value
                                                                    )?.label
                                                                }
                                                            </p>
                                                        )}
                                                    </Field>
                                                )}
                                            </td>
                                            <td
                                                className="relative border border-[#EAECF0] px-4 py-2 font-roboto"
                                                style={{ color: '#333' }}
                                            >
                                                <Field
                                                    name={`quote_answer[quote-${singleData.id}][quantity]`}
                                                >
                                                    {({
                                                        field,
                                                        meta,
                                                        form,
                                                    }: {
                                                        field: React.InputHTMLAttributes<HTMLInputElement> & { name: string };
                                                        form: { setFieldValue: (name: string, value: number | string) => void };
                                                        meta: { error?: string; touched: boolean; };
                                                    }) => (
                                                        <input
                                                            type="number"
                                                            {...field}
                                                            value={handleNumberValue(field.value as string)}
                                                            onChange={(e) =>
                                                                form.setFieldValue(
                                                                    field.name,
                                                                    handleNumberValue(e.target.value)
                                                                )
                                                            }
                                                            className="absolute left-0 top-0 h-full w-full px-5 py-[5px] outline-none"
                                                            style={{
                                                                border:
                                                                    meta.touched && meta.error
                                                                        ? '2px solid #dd0c0cb3'
                                                                        : undefined,
                                                            }}
                                                            placeholder="0"
                                                            min={0}
                                                            onKeyDown={handleKeyDown}
                                                            disabled={disabled}
                                                        />
                                                    )}
                                                </Field>
                                            </td>
                                            <td
                                                className="relative border border-[#EAECF0] px-4 py-2 font-roboto"
                                                style={{ color: '#333' }}
                                            >
                                                <div className="flex">
                                                    <div className="absolute left-2 top-1/2 z-10 -translate-y-1/2">
                                                        $
                                                    </div>
                                                    <Field
                                                        name={`quote_answer[quote-${singleData.id}][rate]`}
                                                    >
                                                        {({
                                                            field,
                                                            meta,
                                                            form,
                                                        }: {
                                                            field: React.InputHTMLAttributes<HTMLInputElement> & { name: string };
                                                            form: { setFieldValue: (name: string, value: number | string) => void };
                                                            meta: { error?: string; touched: boolean; };
                                                        }) => (
                                                            <input
                                                                type="number"
                                                                {...field}
                                                                value={handleNumberValue(field.value as string)}
                                                                onChange={(e) =>
                                                                    form.setFieldValue(
                                                                        field.name,
                                                                        handleNumberValue(e.target.value)
                                                                    )
                                                                }
                                                                className="absolute left-0 right-0 top-0 h-full w-full px-5 py-[5px] pl-5 outline-none"
                                                                style={{
                                                                    border:
                                                                        meta.touched && meta.error
                                                                            ? '2px solid #dd0c0cb3'
                                                                            : undefined,
                                                                }}
                                                                placeholder="0"
                                                                min={0}
                                                                step={1}
                                                                onKeyDown={handleKeyDown}
                                                                disabled={disabled}
                                                            />
                                                        )}
                                                    </Field>
                                                </div>
                                            </td>
                                            <td
                                                className="relative border border-[#EAECF0] px-4 py-2 font-roboto"
                                                style={{ color: '#333' }}
                                            >
                                                <Field
                                                    name={`quote_answer[quote-${singleData.id}][cost]`}
                                                    disabled={true}
                                                >
                                                    {() => (
                                                        <input
                                                            type="text"
                                                            readOnly
                                                            disabled
                                                            className="absolute left-0 top-0 h-full w-full px-5 py-[5px]"
                                                            value={formatCurrency(
                                                                (Number(
                                                                    values?.[`quote-${singleData.id}`]
                                                                        ?.quantity ?? 0
                                                                ) *
                                                                    Number(
                                                                        values?.[`quote-${singleData.id}`]
                                                                            ?.rate ?? 0
                                                                    ))
                                                            )}
                                                        />
                                                    )}
                                                </Field>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                {rfxInviteId && (
                                    <SupplierRowsSection
                                        ref={(handle) => { supplierRowRefs.current[quote.id] = handle; }}
                                        disabled={disabled}
                                        inviteId={rfxInviteId}
                                        isClientView={isClientView}
                                        quoteId={quote.id}
                                        startIndex={clientRows.length}
                                        initialRows={initialSupplierRows}
                                        onRowsChange={(rows) => handleSupplierRowsChange(quote.id, rows)}
                                        onFormikFieldChange={handleFormikFieldChange}
                                    />
                                )}
                                <tbody>
                                    {TOTAL_ROWS.map((list) => (
                                        <tr key={list.id}>
                                            <td
                                                colSpan={isBeingRevised ? 5 : 4}
                                                className="h-[48px] border-none"
                                                style={{ background: '#F9FAFB' }}
                                            />
                                            <td
                                                className="border border-[#EAECF0] bg-gray-utility-100 px-4 py-2"
                                                style={{ color: '#D0D5DD' }}
                                            >
                                                <div
                                                    className="font-roboto font-medium"
                                                    style={{ fontSize: '12px', color: list.color }}
                                                >
                                                    {list.name}
                                                </div>
                                            </td>
                                            <td
                                                className="border border-[#EAECF0] bg-gray-utility-100 px-4 py-2"
                                                style={{ color: '#333' }}
                                            >
                                                {formatCurrency(
                                                    calculateTotal(
                                                        list.id,
                                                        clientRows.map((r) => `quote-${r.id}`),
                                                        values,
                                                        supplierRowCosts[quote.id] ?? 0
                                                    )
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                        {rfxInviteId && (
                            <QuoteComment
                                initialComment={getQuoteComment(quote)}
                                inviteId={rfxInviteId}
                                isReadOnly={disabled || isClientView}
                                quoteId={quote.id}
                            />
                        )}
                        {rfxInviteId && (
                            <QuoteSupportingDocuments
                                initialDocuments={getQuoteAttachments(quote)}
                                inviteId={rfxInviteId}
                                isReadOnly={disabled || isClientView}
                                quoteId={quote.id}
                            />
                        )}
                    </RfxQuoteHeader>
                );
            })}
            {summaryTables.length > 0 && rfxId && (
                <div className="mt-6 space-y-6">
                    {summaryTables.map((st) => (
                        <SummaryTable
                            key={st.id}
                            categoryNames={st.value_column_names}
                            inviteId={rfxInviteId}
                            liveCellValues={summaryLiveCellValues[st.id]}
                            mode={isClientView ? "view" : "respond"}
                            quoteTableIds={st.quote_table_ids}
                            quoteTableNames={listQuotes?.reduce((acc, q) => {
                                acc[q.id] = q.name;
                                return acc;
                            }, {} as Record<number, string>) ?? {}}
                            rfxId={rfxId}
                            summaryTableId={st.id}
                            summaryTableName={st.name}
                        />
                    ))}
                </div>
            )}
            {hasAnyTotalColumn && (
                <div className="mb-10 flex h-[64px] items-center justify-start overflow-hidden rounded-xl border border-solid border-[#EAECF0] bg-white shadow-lg">
                    <div className="h-full content-center border-l-4 border-[#003CE9] pl-10 font-roboto text-[18px] font-semibold text-primary-900">
                        Total
                    </div>
                    <div className="ml-auto h-full content-center border-l border-solid border-[#EAECF0] pl-6 pr-20">
                        <div className="font-roboto text-[12px] font-medium text-primary-900">
                            Subtotal
                        </div>
                        <div
                            className="font-roboto font-medium"
                            style={{ color: '#D0D5DD' }}
                        >
                            {formatCurrency(overallTotals.subtotal)}
                        </div>
                    </div>
                    <div className="h-full content-center border-l border-solid border-[#EAECF0] pl-6 pr-20">
                        <div className="font-roboto text-[12px] font-medium text-primary-900">
                            {GST_RATE_LABEL}
                        </div>
                        <div
                            className="font-roboto font-medium"
                            style={{ color: '#D0D5DD' }}
                        >
                            {formatCurrency(overallTotals.gst)}
                        </div>
                    </div>
                    <div className="h-full content-center border-solid border-[#EAECF0] bg-royal-blue2 pl-6 pr-20">
                        <div
                            className="font-roboto text-[12px] font-medium"
                            style={{ color: '#D0D5DD' }}
                        >
                            Total Cost
                        </div>
                        <div className="font-roboto font-medium text-white">
                            {formatCurrency(overallTotals.totalcost)}
                        </div>
                    </div>
                </div>
            )}
            {rfxInviteId && (
                <ScheduleOfRates
                    ref={scheduleRef}
                    inviteId={rfxInviteId}
                    disabled={disabled}
                    hasValidationError={scheduleValidationError}
                    isClientView={isClientView}
                />
            )}
        </>
    );
});

QuoteTab.displayName = "QuoteTab";

export { QuoteTab };
